import { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExpandCollapseButton } from "@/components/ui/expand-collapse-button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Upload,
  FileText,
  Search,
  Eye,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  FileCheck,
  Loader2,
  History,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Calendar,
  Trash2,
  MessageSquare,
  ClipboardList,
  ClipboardCheck,
  MapPin,
  MoreVertical,
  Mail,
  Pencil,
  X,
  HelpCircle,
  ExternalLink,
  ShieldAlert,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { format } from "date-fns";
import { cn, formatReportDate } from "@/lib/utils";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Link, useSearch, useLocation } from "wouter";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/page-header";
import { ReportQuickSwitcher } from "@/components/report-quick-switcher";
import { InteractionReportQuickSwitcher } from "@/components/interaction-report-quick-switcher";
import { useActiveLocations } from "@/hooks/use-active-locations";
import { useAuth } from "@/lib/auth-context";
import { SearchEmptyState } from "@/components/ui/search-empty-state";
import { PaginationControls } from "@/components/ui/pagination-controls";
import { usePageTitle } from "@/hooks/use-page-title";

interface Report {
  id: string;
  location: "BOSTON" | "SALEM" | "METHUEN" | "CAPE";
  reportDate: string;
  type: string;
  status: "processing" | "completed" | "failed";
  createdAt: string;
  totalAnimals: number;
  pendingAnimalCount?: number | null; // G3.19: Pending-derived count for quarantined reports
  pendingReportId?: string | null; // I1.5: pending_report_data.id for Review link routing
  uploadSequence?: number;
  hasHistory?: boolean;
  needsReviewAt?: string | null; // G3.12: Review state for status display
  sourceEmailId?: string | null; // I9.2: Email ingestion provenance
}

// G1.3: Behavior/Interaction Report interface
interface BehaviorReport {
  id: string;
  reportDate: string;
  locationCode: string | null; // G3.16d: Can be null for quarantined reports
  detectedLocationCode: string | null; // G3.16d: Fallback for quarantined reports
  sourceType: string;
  filename: string | null;
  uploadedAt: string | null;
  createdAt: string;
  diagnostics: {
    totalEvents?: number;
    uniqueAnimals?: number;
    parseWarnings?: string[];
  } | null;
  interactionCount: number;
  pendingInteractionCount: number | null; // G3.17: Pending-derived count for quarantined reports
  needsReviewAt?: string | null; // G3.12: Review state for status display
  sourceEmailId?: string | null; // I9.2: Email ingestion provenance
}

// Gate F18 + F18.1: Location freshness data
interface LocationFreshness {
  locationCode: string;
  locationLabel: string;
  lastUpdatedAtUtc: string | null;
  lastReportLocalDate: string | null;
  isCurrent: boolean;
  staleDays: number;
  // F18.1: Missing dates fields
  missingDatesLocal: string[];
  missingCountTotal: number;
  missingCountReturned: number;
  hasNoBaseline: boolean; // True if location has never had a report
  // Task #99: Report generation timestamp
  generatedAt?: { timestamp: string | null; date: string; confidence: 'exact' | 'date_only' } | null;
}

// Gate F18 + F18.1: Freshness data interface with missing dates
// Gate G3.8: Extended to include Interaction Report freshness
interface FreshnessData {
  serverNowUtc: string;
  expectedDateLocal: string;
  minExpectedReportLocalDate: string; // F18.1: Yesterday (the minimum acceptable date)
  locations: LocationFreshness[];
  summary: {
    anyStale: boolean;
    staleCount: number;
    currentCount: number;
  };
  interaction?: {
    expectedDateLocal?: string;
    locations: LocationFreshness[];
    summary: {
      anyStale: boolean;
      staleCount: number;
      currentCount: number;
    };
  };
}

// Parse EMAIL_INGEST_HOLD review reason for display as badge
function parseEmailIngestHoldReason(reason: string | null): { 
  isEmailIngestHold: boolean; 
  description: string | null;
} {
  if (!reason) return { isEmailIngestHold: false, description: null };
  
  if (reason.startsWith("EMAIL_INGEST_HOLD:")) {
    const afterColon = reason.substring("EMAIL_INGEST_HOLD:".length).trim();
    return { 
      isEmailIngestHold: true, 
      description: afterColon || null 
    };
  }
  
  if (reason.includes("EMAIL_INGEST_HOLD")) {
    const parts = reason.split(";").map(s => s.trim());
    const otherParts = parts.filter(p => !p.includes("EMAIL_INGEST_HOLD"));
    return {
      isEmailIngestHold: true,
      description: otherParts.length > 0 ? otherParts.join("; ") : null,
    };
  }
  
  return { isEmailIngestHold: false, description: reason };
}

// G3.12: StatusBadge now accepts needsReviewAt to show "Review" status when applicable
function StatusBadge({ status, needsReviewAt }: { status: string; needsReviewAt?: string | null }) {
  const variants: Record<string, { variant: any; icon: any; text: string }> = {
    review: { variant: "outline", icon: AlertTriangle, text: "Review" }, // G3.12: Review status
    completed: { variant: "outline", icon: CheckCircle2, text: "Parsed" },
    processing: { variant: "secondary", icon: FileCheck, text: "Processing" },
    failed: { variant: "destructive", icon: XCircle, text: "Failed" },
  };

  // G3.12: If needsReviewAt is set, show "Review" regardless of status
  const effectiveStatus = needsReviewAt ? "review" : status;
  const config = variants[effectiveStatus] || { variant: "outline", icon: FileText, text: effectiveStatus };

  return (
    <Badge 
      variant={config.variant} 
      className={cn(
        "gap-1",
        effectiveStatus === "review" && "text-warning border-warning/30 bg-warning/5",
        effectiveStatus === "completed" && "text-primary border-primary/30 bg-primary/5"
      )}
    >
      <config.icon className="h-3 w-3" />
      {config.text}
    </Badge>
  );
}

// I9.2: SourceBadge for email vs manual upload provenance
function SourceBadge({ sourceEmailId }: { sourceEmailId?: string | null }) {
  if (sourceEmailId) {
    return (
      <Badge 
        variant="outline" 
        className="gap-1 text-xs bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800"
        data-testid="badge-source-email"
      >
        <Mail className="h-3 w-3" />
        Email
      </Badge>
    );
  }
  
  return (
    <Badge 
      variant="outline" 
      className="gap-1 text-xs"
      data-testid="badge-source-manual"
    >
      <Pencil className="h-3 w-3" />
      Manual
    </Badge>
  );
}

// G3.15: Location detection result interface
interface LocationDetectionResult {
  locationCode: string | null;
  locationLabel: string | null;
  isValidLocation: boolean;
  confidence: "high" | "medium" | "low" | "none";
  diagnostics?: {
    zonesFound: string[];
    locationVotes: Record<string, number>;
    warnings: string[];
  };
}

function UploadSection({ onUploadComplete, onClose }: { onUploadComplete: () => void; onClose?: () => void }) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const { toast } = useToast();
  
  // G3.15: Location detection state
  const [detectedLocation, setDetectedLocation] = useState<LocationDetectionResult | null>(null);
  const [isDetecting, setIsDetecting] = useState(false);
  const [detectionError, setDetectionError] = useState<string | null>(null);

  // G3.15: Detect location when file is selected
  useEffect(() => {
    if (!selectedFile) {
      setDetectedLocation(null);
      setDetectionError(null);
      return;
    }

    const detectLocation = async () => {
      setIsDetecting(true);
      setDetectionError(null);
      setDetectedLocation(null);

      try {
        const formData = new FormData();
        formData.append("file", selectedFile);
        
        const result = await apiRequest("POST", "/api/reports/detect-location", formData) as LocationDetectionResult;
        
        if (!result.locationCode || !result.isValidLocation) {
          setDetectionError(
            result.diagnostics?.warnings?.[0] || 
            "Could not detect a valid location from this PDF. Please ensure this is an All Pets report."
          );
        } else {
          setDetectedLocation(result);
        }
      } catch (error) {
        setDetectionError("Failed to analyze PDF. Please try again.");
      } finally {
        setIsDetecting(false);
      }
    };

    detectLocation();
  }, [selectedFile]);

  const uploadMutation = useMutation({
    mutationFn: async ({ file, location }: { file: File; location: string }) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("location", location);
      
      const dateMatch = file.name.match(/(\d{4}-\d{2}-\d{2})/);
      const reportDate = dateMatch ? dateMatch[1] : new Date().toISOString().split('T')[0];
      formData.append("reportDate", reportDate);
      
      return apiRequest("POST", "/api/reports/upload", formData);
    },
    onSuccess: (data: any) => {
      if (data.quarantined) {
        const reviewReasons = data.reviewReasons?.join("; ") || "Some issues were found.";
        toast({
          title: "Report needs review",
          description: reviewReasons,
          action: data.pendingReportId ? (
            <Link href={`/reports/review/all_pets/${data.pendingReportId}`}>
              <Button variant="outline" size="sm" data-testid="button-review-allpets-report">
                Review Report
              </Button>
            </Link>
          ) : undefined,
        });
        queryClient.invalidateQueries({ queryKey: ["/api/reports/pending"] });
        queryClient.invalidateQueries({ queryKey: ["/api/reports/pending/count"] });
      } else {
        toast({
          title: "Upload successful",
          description: "Report is being processed. This may take a few moments.",
        });
      }
      onUploadComplete();
      setSelectedFile(null);
      setDetectedLocation(null);
    },
    onError: () => {
      toast({
        title: "Upload failed",
        description: "Please check the file and try again.",
        variant: "destructive",
      });
    },
  });

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type === "application/pdf") {
      setSelectedFile(file);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setDetectedLocation(null);
    setDetectionError(null);
  };

  const handleUpload = () => {
    if (selectedFile && detectedLocation?.locationCode) {
      uploadMutation.mutate({ file: selectedFile, location: detectedLocation.locationCode });
    }
  };

  const canUpload = selectedFile && detectedLocation?.isValidLocation && !isDetecting && !uploadMutation.isPending;

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
        <div>
          <CardTitle>Upload All Pets Report</CardTitle>
          <CardDescription>Upload a daily All Pets PDF report for processing</CardDescription>
        </div>
        {onClose && (
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="button-close-upload">
            <X className="h-4 w-4" />
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div
            className={cn(
              "border-2 border-dashed rounded-lg p-8 text-center transition-colors",
              isDragging ? "border-primary bg-primary/5" : "border-muted-foreground/25",
              "hover:border-primary hover:bg-primary/5"
            )}
            onDrop={handleDrop}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
          >
            <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-sm font-medium mb-2">
              Drag and drop your PDF report here, or click to browse
            </p>
            <p className="text-xs text-muted-foreground mb-4">
              Only PDF files are accepted (max 10MB)
            </p>
            <Input
              type="file"
              accept=".pdf"
              onChange={handleFileSelect}
              className="hidden"
              id="file-upload"
              data-testid="input-file-upload"
            />
            <Button
              variant="outline"
              onClick={() => document.getElementById("file-upload")?.click()}
              data-testid="button-select-file"
            >
              Select File
            </Button>
          </div>

          {selectedFile && (
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div className="flex items-center gap-3">
                <FileText className="h-8 w-8 text-primary" />
                <div>
                  <p className="text-sm font-medium">{selectedFile.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRemoveFile}
                data-testid="button-remove-file"
              >
                Remove
              </Button>
            </div>
          )}

          {/* G3.15: Location detection status */}
          {selectedFile && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Detected Location</label>
              {isDetecting ? (
                <div className="flex items-center gap-2 p-3 bg-muted rounded-lg" data-testid="location-detecting">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Analyzing PDF...</span>
                </div>
              ) : detectionError ? (
                <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-lg" data-testid="location-error">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm">{detectionError}</span>
                </div>
              ) : detectedLocation?.isValidLocation ? (
                <div className="flex items-center gap-2 p-3 bg-success/10 rounded-lg" data-testid="location-detected">
                  <MapPin className="h-4 w-4 text-success flex-shrink-0" />
                  <span className="text-sm font-medium text-success">{detectedLocation.locationLabel}</span>
                  <Badge variant="outline" className="ml-auto text-xs">
                    {detectedLocation.confidence} confidence
                  </Badge>
                </div>
              ) : null}
            </div>
          )}

          <Button
            className="w-full"
            onClick={handleUpload}
            disabled={!canUpload}
            data-testid="button-upload"
          >
            {uploadMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              "Upload All Pets Report"
            )}
          </Button>

          {uploadMutation.isPending && (
            <div className="space-y-2 p-4 bg-muted/50 rounded-lg" data-testid="upload-progress">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-muted-foreground">
                  Uploading and processing report...
                </span>
              </div>
              <Progress value={75} className="h-2" />
              <p className="text-xs text-muted-foreground">
                This may take a few moments depending on the file size.
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Task #99: Format generatedAt into a human-readable string for the row
function formatGeneratedAt(generatedAt: { timestamp: string | null; date: string; confidence: 'exact' | 'date_only' } | null | undefined): string | null {
  if (!generatedAt) return null;
  if (generatedAt.confidence === 'exact' && generatedAt.timestamp) {
    const d = new Date(generatedAt.timestamp);
    return `${format(d, "MMM d, yyyy")} at ${format(d, "h:mm a")}`;
  }
  try {
    return format(new Date(generatedAt.date + "T00:00:00"), "MMM d, yyyy") + ", time unknown";
  } catch {
    return generatedAt.date + ", time unknown";
  }
}

// Gate F18.1: Stale location row with expandable missing dates
function StaleLocationRow({ loc, uploadSectionId = 'upload-section', onUploadClick }: { loc: FreshnessData['locations'][0]; uploadSectionId?: string; onUploadClick?: () => void }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);
  
  // Only show expand toggle if we have actual dates to show
  const hasMissingDates = loc.missingDatesLocal.length > 0 && loc.missingCountReturned > 0;
  const isCapped = loc.missingCountTotal > loc.missingCountReturned;
  // Use hasNoBaseline flag for "Never" locations (no report history available)
  const isNever = loc.hasNoBaseline;
  
  // Show oldest 10 dates or all if showAll is true
  const missingDates = [...loc.missingDatesLocal].sort((a, b) => a.localeCompare(b));
  const visibleDates = showAll
    ? missingDates
    : missingDates.slice(0, 10);
  const hasMoreDates = loc.missingDatesLocal.length > 10 && !showAll;

  // G3.11: Calmer visual styling - neutral surface with subtle accent
  return (
    <div 
      className="rounded-md border bg-card border-border overflow-hidden"
      data-testid={`row-stale-location-${loc.locationCode}`}
    >
      <div className="flex items-center justify-between p-3">
        <div className="flex items-center gap-3">
          <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0" />
          <div>
            <p className="font-medium text-foreground">{loc.locationLabel}</p>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <span data-testid={`text-generated-at-stale-${loc.locationCode}`}>
                {formatGeneratedAt(loc.generatedAt)
                  ? `Last report: ${formatGeneratedAt(loc.generatedAt)}`
                  : (loc.lastReportLocalDate
                    ? `Last report: ${format(new Date(loc.lastReportLocalDate + "T00:00:00"), "MMM d, yyyy")}`
                    : "Last report: Never"
                  )
                }
              </span>
              {!isNever && (
                <Badge variant="outline" className="text-xs h-5 px-1.5 text-destructive border-destructive/30 bg-destructive/5">
                  {loc.missingCountTotal} day{loc.missingCountTotal > 1 ? 's' : ''} missing
                </Badge>
              )}
              {isNever && (
                <span className="text-muted-foreground italic">
                  Unknown baseline
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {hasMissingDates && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="gap-1"
              data-testid={`button-expand-${loc.locationCode}`}
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              <span className="hidden sm:inline">View dates</span>
            </Button>
          )}
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => {
              if (onUploadClick) {
                onUploadClick();
              }
              // Delay scroll to allow panel to render
              setTimeout(() => {
                const uploadSection = document.getElementById(uploadSectionId);
                if (uploadSection) {
                  uploadSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }, 100);
            }}
            data-testid={`button-upload-stale-${loc.locationCode}`}
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload
          </Button>
        </div>
      </div>
      
      {isExpanded && hasMissingDates && (
        <div className="px-3 pb-3 pt-2 border-t border-border/50 bg-muted/30">
          <div className="flex items-center gap-2 mb-2 text-xs text-muted-foreground">
            <Calendar className="h-3 w-3" />
            <span>
              {isCapped 
                ? `Showing most recent ${loc.missingCountReturned} of ${loc.missingCountTotal} missing dates`
                : `${loc.missingCountTotal} missing date${loc.missingCountTotal > 1 ? 's' : ''}`
              }
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {visibleDates.map((date) => (
              <Badge 
                key={date} 
                variant="outline" 
                className="text-xs bg-background"
                data-testid={`badge-missing-date-${loc.locationCode}-${date}`}
              >
                {format(new Date(date + "T00:00:00"), "MM/dd/yyyy")}
              </Badge>
            ))}
            {hasMoreDates && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-6 text-xs px-2"
                onClick={() => setShowAll(true)}
                data-testid={`button-show-all-${loc.locationCode}`}
              >
                Show all ({loc.missingDatesLocal.length})
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Gate F18.1: Current location row (with optional historical gaps display)
function CurrentLocationRow({ loc }: { loc: FreshnessData['locations'][0] }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);
  
  const hasHistoricalGaps = (loc.missingCountTotal ?? 0) > 0;
  const isCapped = (loc.missingCountTotal ?? 0) > (loc.missingCountReturned ?? 0);
  
  const missingDates = [...(loc.missingDatesLocal ?? [])].sort((a, b) => a.localeCompare(b));
  const visibleDates = showAll
    ? missingDates
    : missingDates.slice(0, 10);
  const hasMoreDates = (loc.missingDatesLocal?.length ?? 0) > 10 && !showAll;

  // G3.11: Calmer visual styling - consistent neutral surface
  return (
    <div 
      className="rounded-md border bg-card border-border overflow-hidden"
      data-testid={`row-current-location-${loc.locationCode}`}
    >
      <div className="flex items-center justify-between p-3">
        <div className="flex items-center gap-3">
          <CheckCircle2 className={`h-4 w-4 ${hasHistoricalGaps ? 'text-warning' : 'text-success'}`} />
          <div>
            <p className="font-medium text-foreground">{loc.locationLabel}</p>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <span data-testid={`text-generated-at-current-${loc.locationCode}`}>
                {formatGeneratedAt(loc.generatedAt)
                  ? `Last report: ${formatGeneratedAt(loc.generatedAt)}`
                  : (loc.lastReportLocalDate
                    ? `Last report: ${format(new Date(loc.lastReportLocalDate + "T00:00:00"), "MMM d, yyyy")}`
                    : "Last report: —"
                  )
                }
              </span>
              {hasHistoricalGaps && (
                <Badge variant="outline" className="text-xs h-5 px-1.5 text-warning border-warning/30 bg-warning/5">
                  {loc.missingCountTotal} historical gap{(loc.missingCountTotal ?? 0) > 1 ? 's' : ''}
                </Badge>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasHistoricalGaps && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="gap-1"
              data-testid={`button-expand-current-${loc.locationCode}`}
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              <span className="hidden sm:inline">View gaps</span>
            </Button>
          )}
          <Badge variant="outline" className={hasHistoricalGaps ? 'text-warning border-warning/20 bg-warning/10' : 'text-success border-success/20 bg-success/10'}>
            {hasHistoricalGaps ? 'Current (with gaps)' : 'Current'}
          </Badge>
        </div>
      </div>
      
      {isExpanded && hasHistoricalGaps && (
        <div className="px-3 pb-3 pt-2 border-t border-border/50 bg-muted/30">
          <div className="flex items-center gap-2 mb-2 text-xs text-muted-foreground">
            <Calendar className="h-3 w-3" />
            <span>
              {isCapped 
                ? `Showing most recent ${loc.missingCountReturned} of ${loc.missingCountTotal} missing dates`
                : `${loc.missingCountTotal} missing date${(loc.missingCountTotal ?? 0) > 1 ? 's' : ''}`
              }
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {visibleDates.map((date) => (
              <Badge 
                key={date} 
                variant="outline" 
                className="text-xs bg-background"
                data-testid={`badge-gap-date-${loc.locationCode}-${date}`}
              >
                {format(new Date(date + "T00:00:00"), "MM/dd/yyyy")}
              </Badge>
            ))}
            {hasMoreDates && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-6 text-xs px-2"
                onClick={() => setShowAll(true)}
                data-testid={`button-show-all-gaps-${loc.locationCode}`}
              >
                Show all ({loc.missingDatesLocal?.length})
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Gate F18.1: Missing Reports Section component
function MissingReportsSection({ 
  freshness, 
  staleLocations, 
  currentLocations, 
  allLocationsUpToDate,
  locationsWithGapsCount 
}: { 
  freshness: FreshnessData;
  staleLocations: FreshnessData['locations'];
  currentLocations: FreshnessData['locations'];
  allLocationsUpToDate: boolean;
  locationsWithGapsCount: number;
}) {
  // Current locations with historical gaps
  const currentWithGaps = currentLocations.filter(loc => (loc.missingCountTotal ?? 0) > 0);
  const currentNoGaps = currentLocations.filter(loc => (loc.missingCountTotal ?? 0) === 0);
  
  return (
    <Card className="mt-6" data-testid="card-missing-reports">
      <CardHeader>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-lg">Missing Reports</CardTitle>
          <Badge 
            variant={allLocationsUpToDate ? "default" : "destructive"}
            data-testid="badge-freshness-summary"
          >
            {allLocationsUpToDate 
              ? "All up to date" 
              : `${locationsWithGapsCount} location${locationsWithGapsCount > 1 ? 's' : ''} with gaps`
            }
          </Badge>
        </div>
        <CardDescription>
          Coverage expected through: {format(new Date(freshness.expectedDateLocal + "T00:00:00"), "MMM d, yyyy")} (today)
        </CardDescription>
      </CardHeader>
      <CardContent>
        {allLocationsUpToDate ? (
          <div className="flex items-center gap-2 p-4 text-success bg-success/10 rounded-md" data-testid="text-all-current">
            <CheckCircle2 className="h-5 w-5" />
            <span>All locations are up to date with no historical gaps.</span>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Stale locations first with expandable missing dates */}
            {staleLocations.map((loc) => (
              <StaleLocationRow key={loc.locationCode} loc={loc} />
            ))}
            {/* Current locations with historical gaps */}
            {currentWithGaps.map((loc) => (
              <CurrentLocationRow key={loc.locationCode} loc={loc} />
            ))}
            {/* Current locations without gaps */}
            {currentNoGaps.map((loc) => (
              <CurrentLocationRow key={loc.locationCode} loc={loc} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// G3.4: Pending Report interface for needs review section
interface PendingReport {
  id: string;
  sourceReportType: string;
  sourceReportId: string;
  reviewStatus: string;
  reviewReason: string | null;
  parsedData: {
    location?: string;
    reportDate?: string;
    animals?: unknown[];
    interactions?: unknown[];
  };
  parseDiagnostics?: {
    totalAnimals?: number;
    totalEvents?: number;
    uniqueAnimals?: number;
    parseWarnings?: string[];
  };
  createdAt: string;
}

// G3.4 + G3.11 + G3.16h: Needs Review Row component - calmer styling consistent with StaleLocationRow
function NeedsReviewRow({ report }: { report: PendingReport }) {
  const reportTypeLabel = report.sourceReportType === "all_pets" ? "All Pets" : "Interaction";
  // Use parseDiagnostics counts first (more reliable), fallback to array lengths
  const itemCount = report.sourceReportType === "all_pets" 
    ? (report.parseDiagnostics?.totalAnimals ?? report.parsedData?.animals?.length ?? 0)
    : (report.parseDiagnostics?.totalEvents ?? report.parsedData?.interactions?.length ?? 0);
  
  // G3.16h: Correct ID selection for review routing
  // For interaction reports, use sourceReportId (behavior_reports.id)
  // For all_pets reports, use pending_report_data.id
  const reviewId = report.sourceReportType === "interaction" 
    ? report.sourceReportId 
    : report.id;
  
  // G3.16h: Defensive handling - check for missing sourceReportId
  const hasMissingSourceReportId = report.sourceReportType === "interaction" && !report.sourceReportId;
  
  // G3.11: Calmer visual styling - neutral surface with subtle accent
  return (
    <div 
      className="rounded-md border bg-card border-border overflow-hidden"
      data-testid={`row-needs-review-${report.id}`}
    >
      <div className="flex items-center justify-between p-3 gap-2 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <AlertTriangle className="h-4 w-4 text-warning flex-shrink-0" />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-medium text-foreground">{report.parsedData?.location || "Unknown"}</p>
              <Badge variant="outline" className="text-xs h-5 px-1.5 text-warning border-warning/30 bg-warning/5">
                {reportTypeLabel}
              </Badge>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <span>
                {report.parsedData?.reportDate 
                  ? format(new Date(report.parsedData.reportDate + "T00:00:00"), "MMM d, yyyy")
                  : "Unknown date"
                }
              </span>
              <span>{itemCount} item{itemCount !== 1 ? "s" : ""}</span>
            </div>
            {report.reviewReason && (() => {
              const parsed = parseEmailIngestHoldReason(report.reviewReason);
              if (parsed.isEmailIngestHold) {
                return (
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-400 dark:border-amber-800" data-testid={`badge-email-hold-${report.id}`}>
                      Email Hold
                    </Badge>
                    {parsed.description && (
                      <span className="text-xs text-muted-foreground line-clamp-1">{parsed.description}</span>
                    )}
                  </div>
                );
              }
              return (
                <p className="text-xs text-warning/80 mt-1 line-clamp-2">
                  {report.reviewReason}
                </p>
              );
            })()}
          </div>
        </div>
        {/* G3.16h: Yellow Review CTA styling matching table and Review Queue */}
        {hasMissingSourceReportId ? (
          <span className="text-xs text-muted-foreground italic">Missing source report reference</span>
        ) : (
          <Button variant="outline" size="sm" asChild className="flex-shrink-0 text-warning border-warning/30">
            <Link 
              href={`/reports/review/${report.sourceReportType}/${reviewId}`} 
              data-testid={`button-review-${report.id}`}
            >
              <ClipboardCheck className="h-4 w-4 mr-2" />
              Review
            </Link>
          </Button>
        )}
      </div>
    </div>
  );
}

// G3.4: Needs Review Section component - styled consistently with MissingReportsSection
function NeedsReviewSection({ 
  pendingReports, 
  isLoading, 
  isError 
}: { 
  pendingReports: PendingReport[];
  isLoading: boolean;
  isError: boolean;
}) {
  if (isLoading) {
    return (
      <div className="space-y-3" data-testid="loading-needs-review">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center gap-2 p-4 text-destructive bg-destructive/10 rounded-md" data-testid="error-needs-review">
        <AlertCircle className="h-5 w-5" />
        <span>Failed to load pending reports.</span>
      </div>
    );
  }

  if (pendingReports.length === 0) {
    return (
      <div className="flex items-center gap-2 p-4 text-success bg-success/10 rounded-md" data-testid="text-no-pending">
        <CheckCircle2 className="h-5 w-5" />
        <span>No reports need review at this time.</span>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="list-needs-review">
      {pendingReports.map((report) => (
        <NeedsReviewRow key={report.id} report={report} />
      ))}
    </div>
  );
}

// G3.4 + G3.8 + G3.10: Reports Alerts Section - Simplified, tab-scoped alerts
// G3.10: Removed nested tabs, show only active tab's alerts
function ReportsAlertsSection({ 
  freshness, 
  staleLocations, 
  currentLocations, 
  allLocationsUpToDate,
  locationsWithGapsCount,
  pendingReports,
  pendingIsLoading,
  pendingIsError,
  pendingCount,
  allPetsPendingCount,
  interactionPendingCount,
  activeReportTab,
  initialExpanded = false, // G3.13: Support deep-linking from dashboard
  onUploadClick // Callback to open upload panel
}: { 
  freshness: FreshnessData;
  staleLocations: LocationFreshness[];
  currentLocations: LocationFreshness[];
  allLocationsUpToDate: boolean;
  locationsWithGapsCount: number;
  pendingReports: PendingReport[];
  pendingIsLoading: boolean;
  pendingIsError: boolean;
  pendingCount: number;
  allPetsPendingCount: number;
  interactionPendingCount: number;
  activeReportTab: string; // "all-pets" or "interactions"
  initialExpanded?: boolean; // G3.13: When true, expand alerts on mount
  onUploadClick?: () => void; // Callback to open upload panel
}) {
  // G3.7: Alerts collapsed by default to reduce visual competition with mode bar
  // G3.13: When initialExpanded is true (from deep-link), start expanded
  const [isCollapsed, setIsCollapsed] = useState(!initialExpanded);
  
  const currentWithGaps = currentLocations.filter(loc => (loc.missingCountTotal ?? 0) > 0);

  // G3.8: Get Interaction Report freshness data
  const interactionLocations = freshness.interaction?.locations || [];
  const interactionStaleLocations = interactionLocations.filter(loc => !loc.isCurrent);
  const interactionCurrentWithGaps = interactionLocations.filter(loc => loc.isCurrent && (loc.missingCountTotal ?? 0) > 0);
  
  // G3.8: Count locations with Interaction Report gaps
  const interactionLocationsWithGaps = interactionLocations.filter(
    loc => !loc.isCurrent || (loc.missingCountTotal ?? 0) > 0 || loc.hasNoBaseline
  );
  const interactionLocationsWithGapsCount = interactionLocationsWithGaps.length;

  // G3.10: Calculate missing REPORTS count (dates), not location count
  // Sum missingCountTotal across all locations for each report type
  const allPetsMissingDatesCount = [...staleLocations, ...currentWithGaps]
    .reduce((sum, loc) => sum + (loc.missingCountTotal ?? 0), 0);
  const interactionMissingDatesCount = interactionLocationsWithGaps
    .reduce((sum, loc) => sum + (loc.missingCountTotal ?? 0), 0);
  
  // G3.10: Tab-scoped counts - only show active tab's alerts
  const activeTabMissingDatesCount = activeReportTab === "all-pets" 
    ? allPetsMissingDatesCount 
    : interactionMissingDatesCount;
  
  // G3.10: Filter pending reports for active tab only
  // Note: Database stores "all_pets" (underscore), not "all-pets" (hyphen)
  const activeTabPendingReports = activeReportTab === "all-pets" 
    ? pendingReports.filter(r => r.sourceReportType === "all_pets")
    : pendingReports.filter(r => r.sourceReportType === "interaction");
  // Use per-type counts from dedicated count queries (not capped by list size)
  const activeTabReviewCount = activeReportTab === "all-pets" ? allPetsPendingCount : interactionPendingCount;
  
  // G3.10: Determine if active tab has alerts
  const allPetsHasAlerts = locationsWithGapsCount > 0;
  const interactionHasAlerts = interactionLocationsWithGapsCount > 0;
  const activeTabHasMissingAlerts = activeReportTab === "all-pets" ? allPetsHasAlerts : interactionHasAlerts;
  const hasAlerts = activeTabMissingDatesCount > 0 || activeTabReviewCount > 0;

  return (
    <Card className="mt-4" data-testid="card-reports-alerts">
      {/* G3.7: Collapsed summary bar - always visible, keyboard accessible */}
      <div 
        role="button"
        tabIndex={0}
        className={cn(
          "flex items-center justify-between gap-4 px-4 py-3 w-full cursor-pointer transition-colors text-left",
          "hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          !isCollapsed && "border-b"
        )}
        onClick={() => setIsCollapsed(!isCollapsed)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setIsCollapsed(!isCollapsed); } }}
        aria-expanded={!isCollapsed}
        aria-controls="reports-alerts-content"
        data-testid="button-collapse-alerts"
      >
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <AlertTriangle className={cn(
              "h-4 w-4",
              hasAlerts ? "text-warning" : "text-muted-foreground"
            )} />
            <span className="text-sm font-medium">
              {activeReportTab === "all-pets" ? "All Pets" : "Interactions"} Alerts
            </span>
          </div>
          {/* G3.10 + G3.11: Tab-scoped counts with calmer visual styling */}
          {hasAlerts ? (
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              {activeTabMissingDatesCount > 0 && (
                <span className="flex items-center gap-1.5">
                  <Badge variant="outline" className="h-5 px-1.5 text-destructive border-destructive/30 bg-destructive/5">
                    {activeTabMissingDatesCount}
                  </Badge>
                  <span>Missing</span>
                </span>
              )}
              {activeTabReviewCount > 0 && (
                <span className="flex items-center gap-1.5">
                  <Badge variant="outline" className="h-5 px-1.5 text-warning border-warning/30 bg-warning/5">
                    {activeTabReviewCount}
                  </Badge>
                  <span>Need Review</span>
                </span>
              )}
            </div>
          ) : (
            <Badge variant="outline" className="h-5 px-1.5 text-success border-success/30 bg-success/5">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              All clear
            </Badge>
          )}
        </div>
        <ExpandCollapseButton
          expanded={!isCollapsed}
          pointerEvents={false}
          tabIndex={-1}
        />
      </div>

      {/* G3.10: Simplified expanded content - inline categories, no nested tabs */}
      {!isCollapsed && (
        <div id="reports-alerts-content">
          <CardContent className="pt-4">
            {/* G3.10 + G3.11: Missing Reports Section with calmer styling */}
            <div className="mb-6" data-testid="section-missing-reports">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium text-foreground">
                  Missing
                </span>
                {activeTabMissingDatesCount > 0 && (
                  <Badge variant="outline" className="h-5 px-1.5 text-destructive border-destructive/30 bg-destructive/5">
                    {activeTabMissingDatesCount}
                  </Badge>
                )}
              </div>
              <CardDescription className="mb-3 text-xs">
                Coverage expected through: {format(new Date(
                  (activeReportTab === "all-pets"
                    ? freshness.expectedDateLocal
                    : freshness.interaction?.expectedDateLocal || freshness.expectedDateLocal
                  ) + "T00:00:00"
                ), "MMM d, yyyy")}{activeReportTab === "all-pets" ? " (today)" : " (yesterday)"}
              </CardDescription>
              {activeReportTab === "all-pets" ? (
                allPetsHasAlerts ? (
                  <div className="space-y-2" data-testid="list-missing-allpets">
                    {staleLocations.map((loc) => (
                      <StaleLocationRow key={`allpets-${loc.locationCode}`} loc={loc} uploadSectionId="upload-section" onUploadClick={onUploadClick} />
                    ))}
                    {currentWithGaps.map((loc) => (
                      <CurrentLocationRow key={`allpets-${loc.locationCode}`} loc={loc} />
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 p-3 text-success bg-success/10 rounded-md text-sm" data-testid="text-allpets-current">
                    <CheckCircle2 className="h-4 w-4" />
                    <span>All Pets reports are up to date.</span>
                  </div>
                )
              ) : (
                interactionHasAlerts ? (
                  <div className="space-y-2" data-testid="list-missing-interaction">
                    {interactionStaleLocations.map((loc) => (
                      <StaleLocationRow key={`interaction-${loc.locationCode}`} loc={loc} uploadSectionId="upload-section-interaction" onUploadClick={onUploadClick} />
                    ))}
                    {interactionCurrentWithGaps.map((loc) => (
                      <CurrentLocationRow key={`interaction-${loc.locationCode}`} loc={loc} />
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 p-3 text-success bg-success/10 rounded-md text-sm" data-testid="text-interaction-current">
                    <CheckCircle2 className="h-4 w-4" />
                    <span>Interactions summaries are up to date.</span>
                  </div>
                )
              )}
            </div>

            {/* G3.10 + G3.11: Needs Review Section with calmer styling */}
            <div data-testid="section-needs-review">
              <div className="flex items-center gap-2 mb-2">
                <ClipboardList className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium text-foreground">
                  Need Review
                </span>
                {activeTabReviewCount > 0 && (
                  <Badge variant="outline" className="h-5 px-1.5 text-warning border-warning/30 bg-warning/5">
                    {activeTabReviewCount}
                  </Badge>
                )}
              </div>
              <CardDescription className="mb-3 text-xs">
                Reports requiring your review before being added to the system
              </CardDescription>
              <NeedsReviewSection 
                pendingReports={activeTabPendingReports}
                isLoading={pendingIsLoading}
                isError={pendingIsError}
              />
            </div>
          </CardContent>
        </div>
      )}
    </Card>
  );
}

// G1.3: Interaction Report Upload Section
function BehaviorUploadSection({ onUploadComplete, onClose }: { onUploadComplete: () => void; onClose?: () => void }) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const { toast } = useToast();

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      return apiRequest("POST", "/api/behavior/upload", formData);
    },
    onSuccess: (data: any) => {
      if (data.status === "duplicate") {
        toast({
          title: "Duplicate report",
          description: "This PDF has already been processed.",
        });
      } else if (data.status === "quarantined") {
        // G3.4: Report needs review - show CTA to review page
        toast({
          title: "Report needs review",
          description: data.warning || "Some issues were found that require your attention.",
          action: data.pendingReportId ? (
            <Link href={`/reports/review/interaction/${data.pendingReportId}`}>
              <Button variant="outline" size="sm" data-testid="button-review-report">
                Review Report
              </Button>
            </Link>
          ) : undefined,
        });
        // Also invalidate review queue count
        queryClient.invalidateQueries({ queryKey: ["/api/reports/pending"] });
      } else {
        toast({
          title: "Upload successful",
          description: `Interaction report processed: ${data.interactions?.inserted || 0} interactions added.`,
        });
      }
      onUploadComplete();
      setSelectedFile(null);
    },
    onError: (error: any) => {
      toast({
        title: "Upload failed",
        description: error?.message || "Please check the file and try again.",
        variant: "destructive",
      });
    },
  });

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type === "application/pdf") {
      setSelectedFile(file);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = () => {
    if (selectedFile) {
      uploadMutation.mutate(selectedFile);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
        <div>
          <CardTitle>Upload Interactions Summary</CardTitle>
          <CardDescription>Upload a Daily Dog Interactions Summary PDF for processing</CardDescription>
        </div>
        {onClose && (
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="button-close-behavior-upload">
            <X className="h-4 w-4" />
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div
            className={cn(
              "border-2 border-dashed rounded-lg p-8 text-center transition-colors",
              isDragging ? "border-primary bg-primary/5" : "border-muted-foreground/25",
              "hover:border-primary hover:bg-primary/5"
            )}
            onDrop={handleDrop}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
          >
            <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-sm font-medium mb-2">
              Drag and drop your Interactions Summary PDF here
            </p>
            <p className="text-xs text-muted-foreground mb-4">
              Only PDF files are accepted (max 10MB)
            </p>
            <Input
              type="file"
              accept=".pdf"
              onChange={handleFileSelect}
              className="hidden"
              id="behavior-file-upload"
              data-testid="input-behavior-file-upload"
            />
            <Button
              variant="outline"
              onClick={() => document.getElementById("behavior-file-upload")?.click()}
              data-testid="button-select-behavior-file"
            >
              Select File
            </Button>
          </div>

          {selectedFile && (
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div className="flex items-center gap-3">
                <FileText className="h-8 w-8 text-primary" />
                <div>
                  <p className="text-sm font-medium">{selectedFile.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedFile(null)}
                data-testid="button-remove-behavior-file"
              >
                Remove
              </Button>
            </div>
          )}

          <Button
            className="w-full"
            onClick={handleUpload}
            disabled={!selectedFile || uploadMutation.isPending}
            data-testid="button-upload-behavior"
          >
            {uploadMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              "Upload Interactions Summary"
            )}
          </Button>

          {uploadMutation.isPending && (
            <div className="space-y-2 p-4 bg-muted/50 rounded-lg" data-testid="behavior-upload-progress">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-muted-foreground">
                  Processing interaction report...
                </span>
              </div>
              <Progress value={75} className="h-2" />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// G1.3: Interaction Reports List Tab
// G3.6: Added filter controls for parity with All Pets tab - accepts shared filter state from parent
// I1.1: Added pagination props for behavior tab
// I1.3: Separate bhSearchQuery for server-side global search
interface InteractionReportsTabProps {
  bhSearchQuery: string;
  handleBhSearchChange: (query: string) => void;
  selectedLocation: string;
  setSelectedLocation: (location: string) => void;
  activeLocations: { id: string; code: string; label: string }[];
  isLocationsLoading: boolean;
  locationsError: Error | null;
  interactionQuickJumpOpen: boolean;
  setInteractionQuickJumpOpen: (open: boolean) => void;
  // I1.1: Pagination props
  bhPage: number;
  bhPageSize: number;
  handleBhPageChange: (page: number) => void;
  handleBhPageSizeChange: (size: number) => void;
  buildBehaviorReportsUrl: () => string;
  // Upload panel visibility
  showUploadPanel: boolean;
  onCloseUpload: () => void;
}

function InteractionReportsTab({ 
  bhSearchQuery, 
  handleBhSearchChange, 
  selectedLocation, 
  setSelectedLocation,
  activeLocations,
  isLocationsLoading,
  locationsError,
  interactionQuickJumpOpen,
  setInteractionQuickJumpOpen,
  bhPage,
  bhPageSize,
  handleBhPageChange,
  handleBhPageSizeChange,
  buildBehaviorReportsUrl,
  showUploadPanel,
  onCloseUpload,
}: InteractionReportsTabProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.isAdmin ?? false;

  // I1.1: Paginated query for behavior reports (includes needs-review via URL builder)
  // I1.3: Added search param to queryKey for server-side global filtering
  const { data: behaviorData, isLoading } = useQuery<PaginatedBehaviorReportsResponse>({
    queryKey: ["/api/behavior/reports", { page: bhPage, limit: bhPageSize, location: selectedLocation !== "all" ? selectedLocation : undefined, includeNeedsReview: true, search: bhSearchQuery.trim() || undefined }],
    queryFn: async () => {
      const res = await fetch(buildBehaviorReportsUrl(), {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch behavior reports");
      return res.json();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (reportId: string) => {
      return apiRequest("POST", `/api/behavior/reports/${reportId}/delete`);
    },
    onSuccess: () => {
      toast({
        title: "Report deleted",
        description: "Report removed. Unreviewed data from this report cleared. Associated behavior interactions excluded from queries.",
      });
      // I1.1: Predicate-based invalidation for all behavior report queries
      queryClient.invalidateQueries({ 
        predicate: (query) => {
          const key = query.queryKey[0];
          return typeof key === 'string' && key.startsWith('/api/behavior/reports');
        }
      });
    },
    onError: (error: any) => {
      toast({
        title: "Delete failed",
        description: error?.message || "Unable to delete the report.",
        variant: "destructive",
      });
    },
  });

  const handleUploadComplete = () => {
    // I1.1: Predicate-based invalidation for all behavior report queries
    queryClient.invalidateQueries({ 
      predicate: (query) => {
        const key = query.queryKey[0];
        return typeof key === 'string' && key.startsWith('/api/behavior/reports');
      }
    });
  };

  // I1.3: Server-side filtering - no client-side filter needed
  const reports = behaviorData?.reports || [];
  const bhPagination = behaviorData?.pagination;

  return (
    <div className={cn("grid gap-6 mt-6", showUploadPanel ? "grid-cols-1 lg:grid-cols-3" : "grid-cols-1")}>
      {showUploadPanel && (
        <div className="lg:col-span-1 scroll-mt-6" id="upload-section-interaction">
          <BehaviorUploadSection onUploadComplete={handleUploadComplete} onClose={onCloseUpload} />
        </div>
      )}

      <div className={cn("space-y-4", showUploadPanel && "lg:col-span-2")}>
        {/* G3.6: Filter controls matching All Pets tab */}
        <Card>
          <CardHeader className="space-y-4">
            {/* Filter label row */}
            <div className="flex items-center gap-2 pb-2 border-b">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Filter</h3>
              <Tooltip>
                <TooltipTrigger>
                  <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p>Use these controls to filter and search interaction reports by location or date.</p>
                </TooltipContent>
              </Tooltip>
              <span className="text-sm text-muted-foreground">—</span>
              <span className="text-sm text-muted-foreground">Interaction reports across all locations.</span>
            </div>
            {/* Filter controls */}
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Search className="h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by location or date..."
                  value={bhSearchQuery}
                  onChange={(e) => handleBhSearchChange(e.target.value)}
                  className="max-w-xs"
                  data-testid="input-behavior-search"
                />
              </div>
              <Select 
                value={selectedLocation} 
                onValueChange={setSelectedLocation}
                disabled={isLocationsLoading}
              >
                <SelectTrigger className="w-[160px]" data-testid="select-behavior-location-filter">
                  {isLocationsLoading ? (
                    <Skeleton className="h-4 w-24" />
                  ) : (
                    <SelectValue placeholder="Location" />
                  )}
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Locations</SelectItem>
                  {locationsError ? (
                    <div className="flex items-center gap-2 px-2 py-1.5 text-sm text-destructive">
                      <AlertCircle className="h-4 w-4" />
                      Unable to load
                    </div>
                  ) : (
                    activeLocations.map((loc) => (
                      <SelectItem key={loc.id} value={loc.code} data-testid={`option-behavior-location-${loc.code.toLowerCase()}`}>
                        {loc.label}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <InteractionReportQuickSwitcher 
                triggerVariant="inline" 
                enableKeyboardShortcut={false}
                externalOpen={interactionQuickJumpOpen}
                onExternalOpenChange={setInteractionQuickJumpOpen}
              />
              {(selectedLocation !== "all" || bhSearchQuery) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedLocation("all");
                    handleBhSearchChange("");
                  }}
                  data-testid="button-clear-behavior-filters"
                >
                  Clear all
                </Button>
              )}
            </div>
          </CardHeader>
        </Card>

        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full table-zebra">
                <thead className="border-b bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Location
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Interactions
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Source
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Uploaded At
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {isLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i}>
                        <td className="px-4 py-3" colSpan={7}>
                          <Skeleton className="h-10 w-full" />
                        </td>
                      </tr>
                    ))
                  ) : reports.length > 0 ? (
                    reports.map((report) => (
                      <tr 
                        key={report.id} 
                        className="transition-colors hover:bg-muted/50"
                        data-testid={`row-behavior-report-${report.id}`}
                      >
                        <td className="px-4 py-3">
                          <span 
                            className="font-medium"
                            data-testid={`text-behavior-date-${report.id}`}
                          >
                            {formatReportDate(report.reportDate)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <Badge 
                            variant="outline"
                            data-testid={`badge-behavior-location-${report.id}`}
                          >
                            {/* G3.16d: Null-safe location display */}
                            {report.locationCode ?? report.detectedLocationCode ?? "Unknown"}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          {/* G3.17: Display pending count for quarantined reports, canonical count otherwise */}
                          <span 
                            className="font-medium"
                            data-testid={`text-behavior-count-${report.id}`}
                          >
                            {report.pendingInteractionCount ?? report.interactionCount}
                          </span> interactions
                        </td>
                        <td className="px-4 py-3">
                          {/* G3.12: Status column for parity with All Pets table */}
                          <StatusBadge status="completed" needsReviewAt={report.needsReviewAt} />
                        </td>
                        <td className="px-4 py-3">
                          <SourceBadge sourceEmailId={report.sourceEmailId} />
                        </td>
                        <td 
                          className="px-4 py-3 text-sm text-muted-foreground"
                          data-testid={`text-behavior-uploaded-${report.id}`}
                        >
                          {report.uploadedAt 
                            ? format(new Date(report.uploadedAt), "MMM dd, h:mm a")
                            : format(new Date(report.createdAt), "MMM dd, h:mm a")
                          }
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  data-testid={`button-actions-behavior-${report.id}`}
                                >
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem asChild>
                                  <Link 
                                    href={`/reports/behavior/${report.id}`}
                                    data-testid={`button-view-behavior-${report.id}`}
                                  >
                                    <Eye className="h-4 w-4 mr-2" />
                                    View Report
                                  </Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.preventDefault();
                                    const url = `/api/behavior/reports/${report.id}/file`;
                                    window.open(url, "_blank", "noopener,noreferrer");
                                  }}
                                  data-testid={`action-open-pdf-${report.id}`}
                                >
                                  <FileText className="h-4 w-4 mr-2" />
                                  Open PDF
                                  <ExternalLink className="h-3 w-3 ml-auto" />
                                </DropdownMenuItem>
                                {isAdmin && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <AlertDialog>
                                      <AlertDialogTrigger asChild>
                                        <DropdownMenuItem 
                                          className="text-destructive focus:text-destructive"
                                          onSelect={(e) => e.preventDefault()}
                                          disabled={deleteMutation.isPending}
                                          data-testid={`button-delete-behavior-${report.id}`}
                                        >
                                          {deleteMutation.isPending ? (
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                          ) : (
                                            <Trash2 className="h-4 w-4 mr-2" />
                                          )}
                                          Delete Report
                                        </DropdownMenuItem>
                                      </AlertDialogTrigger>
                                      <AlertDialogContent>
                                        <AlertDialogHeader>
                                          <AlertDialogTitle>Delete Interaction Report</AlertDialogTitle>
                                          <AlertDialogDescription>
                                            Are you sure you want to delete this interaction report from {report.locationCode ?? report.detectedLocationCode ?? "Unknown"} dated {formatReportDate(report.reportDate)}?
                                            <br /><br />
                                            {report.needsReviewAt ? (
                                              <>This report is still under review and has not been processed. Deleting it will discard the uploaded data.</>
                                            ) : (
                                              <>This will permanently remove the interaction records from this report.</>
                                            )}
                                          </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                                          <AlertDialogAction
                                            onClick={() => deleteMutation.mutate(report.id)}
                                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                            data-testid={`button-confirm-delete-behavior-${report.id}`}
                                          >
                                            Delete Report
                                          </AlertDialogAction>
                                        </AlertDialogFooter>
                                      </AlertDialogContent>
                                    </AlertDialog>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                            {/* G3.12: Show Review action when report needs review */}
                            {report.needsReviewAt && (
                              <Button variant="outline" size="sm" className="text-warning border-warning/30" asChild>
                                <Link 
                                  href={`/reports/review/interaction/${report.id}`}
                                  data-testid={`button-review-behavior-${report.id}`}
                                >
                                  <ClipboardCheck className="h-4 w-4 mr-2" />
                                  Review
                                </Link>
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="px-4 py-8">
                        {(selectedLocation !== "all" || bhSearchQuery) ? (
                          <SearchEmptyState />
                        ) : (
                          <div className="text-center text-muted-foreground">
                            No interaction reports uploaded yet. Upload a report to get started.
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {/* I1.1: Behavior Reports Pagination Controls - using shared component */}
            {bhPagination && bhPagination.totalPages > 0 && (
              <PaginationControls
                currentPage={bhPage}
                totalPages={bhPagination.totalPages}
                total={bhPagination.total}
                pageSize={bhPageSize}
                startIndex={reports.length > 0 ? ((bhPage - 1) * bhPageSize + 1) : 0}
                endIndex={Math.min((bhPage - 1) * bhPageSize + reports.length, bhPagination.total)}
                onPageChange={handleBhPageChange}
                onPageSizeChange={handleBhPageSizeChange}
                itemLabel="reports"
                testIdPrefix="behavior"
                className="border-t"
              />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// I1: Paginated response interface for All Pets reports
interface PaginatedReportsResponse {
  items: Report[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// I1: Paginated response interface for Behavior reports
interface PaginatedBehaviorReportsResponse {
  reports: BehaviorReport[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export default function Reports() {
  usePageTitle("Reports");
  // Gate F13: Read initial location and tab from URL search params
  // G3.13: Read alerts deep-link params
  // I1: Read pagination params from URL
  const searchString = useSearch();
  const urlParams = new URLSearchParams(searchString);
  
  // I1.1: Parse URL params with per-tab pagination (apPage/apLimit for All Pets, bhPage/bhLimit for Behavior)
  const urlApPage = parseInt(urlParams.get("apPage") || "1", 10) || 1;
  const urlApLimit = parseInt(urlParams.get("apLimit") || "10", 10) || 10;
  const urlBhPage = parseInt(urlParams.get("bhPage") || "1", 10) || 1;
  const urlBhLimit = parseInt(urlParams.get("bhLimit") || "10", 10) || 10;
  const urlLocation = urlParams.get("location") || "all";
  const urlTab = urlParams.get("tab") || "all-pets";
  
  // G3.13: Deep-link from dashboard to expand alerts section
  const alertsParam = urlParams.get("alerts") === "1";
  const alertsTabParam = urlParams.get("alertsTab"); // "missing" or future options
  const uploadParam = urlParams.get("upload") === "true";
  
  const [selectedLocation, setSelectedLocation] = useState<string>(urlLocation);
  const [searchQuery, setSearchQuery] = useState("");
  const [bhSearchQuery, setBhSearchQuery] = useState(""); // I1.3: Separate search state for Behavior tab
  const [activeTab, setActiveTab] = useState<string>(urlTab);
  const [showUploadPanel, setShowUploadPanel] = useState(uploadParam); // Auto-open if upload=true in URL
  
  // I1.1: Per-tab pagination state
  const [apPage, setApPage] = useState(urlApPage);
  const [apPageSize, setApPageSize] = useState(urlApLimit);
  const [bhPage, setBhPage] = useState(urlBhPage);
  const [bhPageSize, setBhPageSize] = useState(urlBhLimit);
  
  // I1: Track if URL change was triggered by user action vs browser navigation
  const isUserNavigation = useRef(true);
  
  // I1.1: Sync local state with URL params when browser navigation changes (back/forward)
  useEffect(() => {
    // Mark as browser navigation (not user action) to prevent pushing duplicate history
    const hasChanges = 
      urlApPage !== apPage || urlApLimit !== apPageSize || 
      urlBhPage !== bhPage || urlBhLimit !== bhPageSize ||
      urlLocation !== selectedLocation || urlTab !== activeTab;
    if (hasChanges) {
      isUserNavigation.current = false;
    }
    if (urlApPage !== apPage) setApPage(urlApPage);
    if (urlApLimit !== apPageSize) setApPageSize(urlApLimit);
    if (urlBhPage !== bhPage) setBhPage(urlBhPage);
    if (urlBhLimit !== bhPageSize) setBhPageSize(urlBhLimit);
    if (urlLocation !== selectedLocation) setSelectedLocation(urlLocation);
    if (urlTab !== activeTab) setActiveTab(urlTab);
    if (uploadParam) {
      setShowUploadPanel(true);
      const cleanParams = new URLSearchParams(searchString);
      cleanParams.delete("upload");
      const cleanUrl = `/reports${cleanParams.toString() ? `?${cleanParams.toString()}` : ""}`;
      window.history.replaceState(null, "", cleanUrl);
      setTimeout(() => {
        const tab = urlParams.get("tab") || "all-pets";
        const sectionId = tab === 'interactions' ? 'upload-section-interaction' : 'upload-section';
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
    }
  }, [searchString]); // Re-run when URL changes
  const { toast } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.isAdmin ?? false;

  // Context-aware Quick Jump: same keyboard shortcut opens the appropriate dialog based on active tab
  const [allPetsQuickJumpOpen, setAllPetsQuickJumpOpen] = useState(false);
  const [interactionQuickJumpOpen, setInteractionQuickJumpOpen] = useState(false);

  const handleQuickJumpKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && e.key === "k") {
        const target = e.target as HTMLElement;
        const tagName = target.tagName.toLowerCase();
        const isEditable =
          tagName === "input" ||
          tagName === "textarea" ||
          target.isContentEditable;

        if (!isEditable) {
          e.preventDefault();
          if (activeTab === "all-pets") {
            setAllPetsQuickJumpOpen(true);
          } else if (activeTab === "interactions") {
            setInteractionQuickJumpOpen(true);
          }
        }
      }
    },
    [activeTab]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleQuickJumpKeyDown);
    return () => document.removeEventListener("keydown", handleQuickJumpKeyDown);
  }, [handleQuickJumpKeyDown]);

  // Gate F6.6: Dynamic locations from API
  const { locations: activeLocations, isLoading: isLocationsLoading, error: locationsError } = useActiveLocations();
  const [, navigate] = useLocation();

  // I1.1: Build All Pets pagination query URL
  // I1.3: Added server-side search param
  // I1.4: Added includeNeedsReview=true to include quarantined reports in table
  const buildReportsUrl = useCallback(() => {
    const params = new URLSearchParams();
    params.set("page", String(apPage));
    params.set("limit", String(apPageSize));
    params.set("includeNeedsReview", "true"); // I1.4: Include quarantined reports
    if (selectedLocation !== "all") {
      params.set("location", selectedLocation);
    }
    if (searchQuery.trim()) {
      params.set("search", searchQuery.trim());
    }
    return `/api/reports?${params.toString()}`;
  }, [apPage, apPageSize, selectedLocation, searchQuery]);
  
  // I1.1: Build Behavior reports pagination query URL
  // I1.3: Added server-side search param for global search
  const buildBehaviorReportsUrl = useCallback(() => {
    const params = new URLSearchParams();
    params.set("page", String(bhPage));
    params.set("limit", String(bhPageSize));
    params.set("includeNeedsReview", "true"); // G3.12a: Include quarantined reports
    if (selectedLocation !== "all") {
      params.set("location", selectedLocation);
    }
    if (bhSearchQuery.trim()) {
      params.set("search", bhSearchQuery.trim());
    }
    return `/api/behavior/reports?${params.toString()}`;
  }, [bhPage, bhPageSize, selectedLocation, bhSearchQuery]);

  // I1.1: Build browser URL with per-tab pagination params
  const buildBrowserUrl = useCallback(() => {
    const params = new URLSearchParams();
    if (activeTab !== "all-pets") params.set("tab", activeTab);
    if (selectedLocation !== "all") params.set("location", selectedLocation);
    // All Pets pagination
    if (apPage > 1) params.set("apPage", String(apPage));
    if (apPageSize !== 10) params.set("apLimit", String(apPageSize));
    // Behavior pagination
    if (bhPage > 1) params.set("bhPage", String(bhPage));
    if (bhPageSize !== 10) params.set("bhLimit", String(bhPageSize));
    if (alertsParam) params.set("alerts", "1");
    const queryString = params.toString();
    return queryString ? `/reports?${queryString}` : "/reports";
  }, [activeTab, selectedLocation, apPage, apPageSize, bhPage, bhPageSize, alertsParam]);
  
  // I1: Update URL when pagination state changes (push for user actions, skip for browser nav sync)
  useEffect(() => {
    const newUrl = buildBrowserUrl();
    const currentUrl = window.location.pathname + window.location.search;
    
    // Only update URL if it's different from current
    if (newUrl !== currentUrl) {
      if (isUserNavigation.current) {
        // User triggered pagination change - push to history
        window.history.pushState(null, "", newUrl);
      } else {
        // Browser navigation - reset flag for next user action
        isUserNavigation.current = true;
      }
    }
  }, [buildBrowserUrl]);

  // I1.1: Paginated query for All Pets reports
  // I1.3: Added search param to queryKey for server-side filtering
  // I1.4: Added includeNeedsReview to queryKey for cache consistency
  const { data: reportsData, isLoading, refetch } = useQuery<PaginatedReportsResponse>({
    queryKey: ["/api/reports", { page: apPage, limit: apPageSize, location: selectedLocation !== "all" ? selectedLocation : undefined, search: searchQuery.trim() || undefined, includeNeedsReview: true }],
    queryFn: async () => {
      const res = await fetch(buildReportsUrl(), { credentials: "include" });
      if (!res.ok) {
        throw new Error("Failed to fetch reports");
      }
      return res.json();
    },
  });
  
  const reports = reportsData?.items || [];
  const apPagination = reportsData?.pagination;

  // Gate G1: Delete report mutation (admin only)
  const deleteMutation = useMutation({
    mutationFn: async (reportId: string) => {
      return apiRequest("POST", `/api/reports/${reportId}/delete`);
    },
    onSuccess: (data: any, reportId) => {
      const affectedCount = data?.affectedAnimals ?? 0;
      const parts: string[] = [
        "Report removed.",
        "Unreviewed data from this report cleared.",
      ];
      if (affectedCount > 0) {
        parts.push(`Animal records updated for ${affectedCount} animal${affectedCount !== 1 ? "s" : ""}.`);
      }
      parts.push("Capacity snapshots recalculated.");
      parts.push("Animal stay records are being recalculated in the background.");
      toast({
        title: "Report deleted",
        description: parts.join(" "),
      });
      // I1: Invalidate all paginated report queries (predicate matches any key starting with /api/reports)
      queryClient.invalidateQueries({ 
        predicate: (query) => {
          const key = query.queryKey[0];
          return typeof key === 'string' && (
            key.startsWith('/api/reports') || 
            key === '/api/dashboard/freshness' ||
            key === '/api/dashboard/metrics' ||
            key === '/api/animals'
          );
        }
      });
    },
    onError: (error: any) => {
      toast({
        title: "Delete failed",
        description: error?.message || "Unable to delete the report. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Gate F18: Fetch freshness data for missing reports section
  const { data: freshness } = useQuery<FreshnessData>({
    queryKey: ["/api/dashboard/freshness"],
  });

  // Gate F18 + F18.1: Get stale and current locations with proper sorting
  // Sort order: hasNoBaseline first, then by missingCountTotal desc, then by label asc
  const staleLocations = (freshness?.locations?.filter(loc => !loc.isCurrent) || [])
    .sort((a, b) => {
      // 1) hasNoBaseline first
      if (a.hasNoBaseline && !b.hasNoBaseline) return -1;
      if (!a.hasNoBaseline && b.hasNoBaseline) return 1;
      // 2) missingCountTotal descending
      const countA = a.missingCountTotal ?? 0;
      const countB = b.missingCountTotal ?? 0;
      if (countB !== countA) return countB - countA;
      // 3) locationLabel alphabetically
      return a.locationLabel.localeCompare(b.locationLabel);
    });
  
  // Current locations: also check for historical gaps
  // Sort by missingCountTotal desc, then by label
  const currentLocations = (freshness?.locations?.filter(loc => loc.isCurrent) || [])
    .sort((a, b) => {
      const countA = a.missingCountTotal ?? 0;
      const countB = b.missingCountTotal ?? 0;
      if (countB !== countA) return countB - countA;
      return a.locationLabel.localeCompare(b.locationLabel);
    });
  
  // Count locations with gaps (either stale OR current with historical gaps)
  const locationsWithGaps = freshness?.locations?.filter(
    loc => !loc.isCurrent || (loc.missingCountTotal ?? 0) > 0 || loc.hasNoBaseline
  ) || [];
  const allLocationsUpToDate = locationsWithGaps.length === 0 && (freshness?.locations?.length || 0) > 0;

  // I1.3: Search is now server-side - reports are already filtered by the API
  // No client-side filtering needed for All Pets tab
  const filteredReports = reports;

  const handleUploadComplete = () => {
    // I1: Invalidate all paginated queries (predicate matches any key starting with /api/reports)
    queryClient.invalidateQueries({ 
      predicate: (query) => {
        const key = query.queryKey[0];
        return typeof key === 'string' && (
          key.startsWith('/api/reports') || 
          key === '/api/dashboard/freshness' ||
          key === '/api/dashboard/metrics' ||
          key === '/api/animals'
        );
      }
    });
    // Reset to page 1 after upload
    setApPage(1);
  };
  
  // I1.1: All Pets pagination helpers
  const handleApPageChange = (newPage: number) => {
    setApPage(newPage);
  };
  
  const handleApPageSizeChange = (newSize: number) => {
    setApPageSize(newSize);
    setApPage(1); // Reset to first page when changing page size
  };
  
  const handleLocationChange = (newLocation: string) => {
    setSelectedLocation(newLocation);
    setApPage(1); // Reset All Pets to first page when changing location filter
    setBhPage(1); // Reset Behavior to first page when changing location filter
  };
  
  // I1.3: Search change handler - resets page to 1 for server-side search
  const handleSearchChange = (newSearch: string) => {
    setSearchQuery(newSearch);
    // Reset page to 1 when search changes (for whichever tab is active)
    if (activeTab === "all-pets") {
      setApPage(1);
    } else {
      setBhPage(1);
    }
  };
  
  // I1.1: Behavior pagination helpers
  const handleBhPageChange = (newPage: number) => {
    setBhPage(newPage);
  };
  
  const handleBhPageSizeChange = (newSize: number) => {
    setBhPageSize(newSize);
    setBhPage(1); // Reset to first page when changing page size
  };
  
  // I1.3: Behavior search change handler - resets page to 1 for server-side search
  const handleBhSearchChange = (newSearch: string) => {
    setBhSearchQuery(newSearch);
    setBhPage(1); // Reset to first page when search changes
  };

  // G3.4: Fetch pending review count for badge (total across all types)
  const { data: pendingCountData } = useQuery<{ count: number }>({
    queryKey: ["/api/reports/pending/count"],
  });
  const pendingReviewCount = pendingCountData?.count || 0;

  // G3.4: Per-type counts so the data freshness section shows accurate numbers (not capped by list limit)
  const { data: allPetsPendingCountData } = useQuery<{ count: number }>({
    queryKey: ["/api/reports/pending/count?type=all_pets"],
  });
  const { data: interactionPendingCountData } = useQuery<{ count: number }>({
    queryKey: ["/api/reports/pending/count?type=interaction"],
  });
  const allPetsPendingCount = allPetsPendingCountData?.count || 0;
  const interactionPendingCount = interactionPendingCountData?.count || 0;

  // G3.4: Fetch pending reports list for alerts section (oldest first, up to 200)
  const { data: pendingReportsData, isLoading: pendingIsLoading, isError: pendingIsError } = useQuery<{ 
    items: PendingReport[]; 
    pagination: { total: number };
  }>({
    queryKey: ["/api/reports/pending?limit=200"],
  });
  const pendingReports = pendingReportsData?.items || [];

  // P28a: Ingest gate notice
  const { data: metaData } = useQuery<{ ingestHoldEnabled: boolean; ingestHoldReason?: string | null }>({
    queryKey: ["/api/app-metadata"],
  });

  return (
    <div className="px-4 sm:px-6 pb-6 pt-4">
      <PageHeader
        breadcrumb={[{ label: "Reporting" }, { label: "Reports" }]}
        title={
          <span className="flex items-center gap-2" data-testid="heading-reports">
            <FileText className="w-6 h-6 text-primary" />
            Reports
          </span>
        }
        actions={
          pendingReviewCount > 0 ? (
            <Link href="/reports/review">
              <Button variant="outline" size="sm" className="gap-2" data-testid="button-pending-reviews">
                <ClipboardList className="h-4 w-4" />
                Reports Need Review
              </Button>
            </Link>
          ) : undefined
        }
      />

      {/* P28a: Ingest gate notice */}
      {metaData?.ingestHoldEnabled && (
        <div
          className="mt-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300"
          data-testid="banner-ingest-gate-reports"
        >
          <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" />
          <p className="text-sm">
            <span className="font-semibold">Report ingestion is currently disabled.</span>{" "}
            {metaData.ingestHoldReason
              ? metaData.ingestHoldReason + " "
              : "New PDF reports are being held and will not be processed automatically. "}
            Contact a super admin for details.
          </p>
        </div>
      )}

      {/* G3.7: Report Type Mode Bar - Primary navigation for report types, positioned above alerts */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <TabsList className="h-11 p-1" data-testid="tabs-reports">
            <TabsTrigger 
              value="all-pets" 
              className="h-9 px-4 text-sm font-medium gap-2"
              data-testid="tab-all-pets"
            >
              <FileText className="h-4 w-4" />
              All Pets Report
            </TabsTrigger>
            <TabsTrigger 
              value="interactions" 
              className="h-9 px-4 text-sm font-medium gap-2"
              data-testid="tab-interactions"
            >
              <MessageSquare className="h-4 w-4" />
              Interactions Summary
            </TabsTrigger>
          </TabsList>
          <Button 
            variant="default" 
            size="sm" 
            className="gap-2"
            onClick={() => {
              setShowUploadPanel(true);
              setTimeout(() => {
                const sectionId = activeTab === 'interactions' ? 'upload-section-interaction' : 'upload-section';
                document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 100);
            }}
            data-testid="button-upload-report"
          >
            <Upload className="h-4 w-4" />
            Upload Report
          </Button>
        </div>

      {/* G3.4 + G3.7 + G3.8 + G3.10: Reports Alerts Section - Tab-scoped, simplified */}
      {freshness && (
        <ReportsAlertsSection 
          freshness={freshness}
          staleLocations={staleLocations}
          currentLocations={currentLocations}
          allLocationsUpToDate={allLocationsUpToDate}
          locationsWithGapsCount={locationsWithGaps.length}
          pendingReports={pendingReports}
          pendingIsLoading={pendingIsLoading}
          pendingIsError={pendingIsError}
          pendingCount={pendingReviewCount}
          allPetsPendingCount={allPetsPendingCount}
          interactionPendingCount={interactionPendingCount}
          activeReportTab={activeTab}
          initialExpanded={alertsParam} // G3.13: Auto-expand when deep-linked from dashboard
          onUploadClick={() => setShowUploadPanel(true)}
        />
      )}

        <TabsContent value="all-pets">
          <div className={cn("grid gap-6 mt-6", showUploadPanel ? "grid-cols-1 lg:grid-cols-3" : "grid-cols-1")}>
        {showUploadPanel && (
          <div className="lg:col-span-1 scroll-mt-6" id="upload-section">
            <UploadSection onUploadComplete={handleUploadComplete} onClose={() => setShowUploadPanel(false)} />
          </div>
        )}

        <div className={cn("space-y-4", showUploadPanel && "lg:col-span-2")}>
          <Card>
            <CardHeader className="space-y-4">
              {/* Filter label row */}
              <div className="flex items-center gap-2 pb-2 border-b">
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Filter</h3>
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>Use these controls to filter and search All Pets reports by location or date.</p>
                  </TooltipContent>
                </Tooltip>
                <span className="text-sm text-muted-foreground">—</span>
                <span className="text-sm text-muted-foreground">All Pets reports across all locations.</span>
              </div>
              {/* Filter controls */}
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <Search className="h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by location or date..."
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="max-w-xs"
                    data-testid="input-search"
                  />
                </div>
                <Select 
                  value={selectedLocation} 
                  onValueChange={handleLocationChange}
                  disabled={isLocationsLoading}
                >
                  <SelectTrigger className="w-[160px]" data-testid="select-location-filter">
                    {isLocationsLoading ? (
                      <Skeleton className="h-4 w-24" />
                    ) : (
                      <SelectValue placeholder="Location" />
                    )}
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Locations</SelectItem>
                    {locationsError ? (
                      <div className="flex items-center gap-2 px-2 py-1.5 text-sm text-destructive">
                        <AlertCircle className="h-4 w-4" />
                        Unable to load
                      </div>
                    ) : (
                      activeLocations.map((loc) => (
                        <SelectItem key={loc.id} value={loc.code} data-testid={`option-location-${loc.code.toLowerCase()}`}>
                          {loc.label}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <ReportQuickSwitcher 
                  triggerVariant="inline" 
                  enableKeyboardShortcut={false}
                  externalOpen={allPetsQuickJumpOpen}
                  onExternalOpenChange={setAllPetsQuickJumpOpen}
                />
                {(selectedLocation !== "all" || searchQuery) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      handleLocationChange("all");
                      handleSearchChange("");
                    }}
                    data-testid="button-clear-filters"
                  >
                    Clear all
                  </Button>
                )}
              </div>
            </CardHeader>
          </Card>

          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full table-zebra">
                  <thead className="border-b bg-muted/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                        Location
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                        Total Animals
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                        Source
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                        Uploaded At
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {isLoading ? (
                      Array.from({ length: 5 }).map((_, i) => (
                        <tr key={i}>
                          <td className="px-4 py-3" colSpan={7}>
                            <Skeleton className="h-10 w-full" />
                          </td>
                        </tr>
                      ))
                    ) : filteredReports && filteredReports.length > 0 ? (
                      filteredReports.map((report) => (
                        <tr key={report.id} className="transition-colors hover:bg-muted/50" data-testid={`row-report-${report.id}`}>
                          <td className="px-4 py-3" data-testid={`text-date-${report.id}`}>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{formatReportDate(report.reportDate)}</span>
                              {report.hasHistory && (
                                <Badge 
                                  variant="outline" 
                                  className="text-xs gap-1"
                                  data-testid={`badge-history-${report.id}`}
                                >
                                  <History className="h-3 w-3" />
                                  v{report.uploadSequence}
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant="outline" data-testid={`badge-location-${report.id}`}>
                              {report.location}
                            </Badge>
                          </td>
                          <td className="px-4 py-3" data-testid={`text-count-${report.id}`}>
                            <span className="font-medium">{report.pendingAnimalCount ?? report.totalAnimals}</span> animals
                          </td>
                          <td className="px-4 py-3">
                            <StatusBadge status={report.status} needsReviewAt={report.needsReviewAt} />
                          </td>
                          <td className="px-4 py-3">
                            <SourceBadge sourceEmailId={report.sourceEmailId} />
                          </td>
                          <td className="px-4 py-3 text-sm text-muted-foreground">
                            {format(new Date(report.createdAt), "MMM dd, h:mm a")}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button 
                                    variant="ghost" 
                                    size="icon"
                                    data-testid={`button-actions-${report.id}`}
                                  >
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem asChild>
                                    <Link href={`/reports/${report.id}`} data-testid={`button-view-${report.id}`}>
                                      <Eye className="h-4 w-4 mr-2" />
                                      View Report
                                    </Link>
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={(e) => {
                                      e.preventDefault();
                                      const url = `/api/reports/${report.id}/file`;
                                      window.open(url, "_blank", "noopener,noreferrer");
                                    }}
                                    data-testid={`action-open-pdf-${report.id}`}
                                  >
                                    <FileText className="h-4 w-4 mr-2" />
                                    Open PDF
                                    <ExternalLink className="h-3 w-3 ml-auto" />
                                  </DropdownMenuItem>
                                  {isAdmin && (
                                    <>
                                      <DropdownMenuSeparator />
                                      <AlertDialog>
                                        <AlertDialogTrigger asChild>
                                          <DropdownMenuItem 
                                            className="text-destructive focus:text-destructive"
                                            onSelect={(e) => e.preventDefault()}
                                            disabled={deleteMutation.isPending}
                                            data-testid={`button-delete-${report.id}`}
                                          >
                                            {deleteMutation.isPending ? (
                                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                            ) : (
                                              <Trash2 className="h-4 w-4 mr-2" />
                                            )}
                                            Delete Report
                                          </DropdownMenuItem>
                                        </AlertDialogTrigger>
                                        <AlertDialogContent>
                                          <AlertDialogHeader>
                                            <AlertDialogTitle>Delete Report</AlertDialogTitle>
                                            <AlertDialogDescription>
                                              Are you sure you want to delete this report from {report.location} dated {formatReportDate(report.reportDate)}?
                                              <br /><br />
                                              {report.needsReviewAt ? (
                                                <>This report is still under review and has not been processed. Deleting it will discard the uploaded data.</>
                                              ) : (
                                                <>This will remove the report from all counts and statistics. Animals from this report will be updated to show their status from other available reports.</>
                                              )}
                                            </AlertDialogDescription>
                                          </AlertDialogHeader>
                                          <AlertDialogFooter>
                                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                                            <AlertDialogAction
                                              onClick={() => deleteMutation.mutate(report.id)}
                                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                              data-testid={`button-confirm-delete-${report.id}`}
                                            >
                                              Delete Report
                                            </AlertDialogAction>
                                          </AlertDialogFooter>
                                        </AlertDialogContent>
                                      </AlertDialog>
                                    </>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                              {/* G3.12: Show Review action when report needs review */}
                              {/* I1.5: Use pendingReportId for Review link (pending_report_data.id, not reports.id) */}
                              {report.needsReviewAt && report.pendingReportId && (
                                <Button variant="outline" size="sm" className="text-warning border-warning/30" asChild>
                                  <Link href={`/reports/review/all_pets/${report.pendingReportId}`} data-testid={`button-review-${report.id}`}>
                                    <ClipboardCheck className="h-4 w-4 mr-2" />
                                    Review
                                  </Link>
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7} className="px-4 py-8">
                          {(selectedLocation !== "all" || searchQuery) ? (
                            <SearchEmptyState />
                          ) : (
                            <div className="text-center text-muted-foreground">
                              No reports uploaded yet. Upload a report to get started.
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {/* I1.1: All Pets Pagination Controls - using shared component */}
              {apPagination && apPagination.totalPages > 0 && (
                <PaginationControls
                  currentPage={apPage}
                  totalPages={apPagination.totalPages}
                  total={apPagination.total}
                  pageSize={apPageSize}
                  startIndex={reports.length > 0 ? ((apPage - 1) * apPageSize + 1) : 0}
                  endIndex={Math.min((apPage - 1) * apPageSize + reports.length, apPagination.total)}
                  onPageChange={handleApPageChange}
                  onPageSizeChange={handleApPageSizeChange}
                  itemLabel="reports"
                  testIdPrefix="allpets"
                  className="border-t"
                />
              )}
            </CardContent>
          </Card>
        </div>
      </div>
        </TabsContent>

        <TabsContent value="interactions">
          <InteractionReportsTab 
            bhSearchQuery={bhSearchQuery}
            handleBhSearchChange={handleBhSearchChange}
            selectedLocation={selectedLocation}
            setSelectedLocation={setSelectedLocation}
            activeLocations={activeLocations}
            isLocationsLoading={isLocationsLoading}
            locationsError={locationsError}
            interactionQuickJumpOpen={interactionQuickJumpOpen}
            setInteractionQuickJumpOpen={setInteractionQuickJumpOpen}
            bhPage={bhPage}
            bhPageSize={bhPageSize}
            handleBhPageChange={handleBhPageChange}
            handleBhPageSizeChange={handleBhPageSizeChange}
            buildBehaviorReportsUrl={buildBehaviorReportsUrl}
            showUploadPanel={showUploadPanel}
            onCloseUpload={() => setShowUploadPanel(false)}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
